import { useContext, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Context from '../../context';
import { generateNestedPreviewUrl } from '../../helpers';
import './Slide.scss';
import Navbar from "../global/Navbar";
import Home from '../Home';
import DailyFlights from "./Flights";
import DailyPosts from "./Posts";
import DailyMap from "./Map";
import DailyWeather from "./Weather";
import DailySummary from "./Summary";

const Slide = ({ pathStart, homeComponent, type, isActive, ...props}) => {
  const [activeSection, setActiveSection] = useState('summary');
  const [style, setStyle] = useState({ backgroundColor: '#2E2F2E' });
  const { data, slideNum } = props;
  const { itin, icons, viewMode, t, mobileViewGeneric, previewMode } = useContext(Context);

  const location = useLocation();
  useEffect(() => {
    let isActive = true;
    if (mobileViewGeneric) {
      setStyle({ backgroundColor: 'white' });
    } else {
      if (!data?.imageNodeId && (data?.type === "file" && !data.file.httpsUrl) && !itin?.photoNode?.file?.httpsUrl) return;
      const img = new Image();
      img.onload = () => {
        if (isActive) {
          const darkening = homeComponent && (itin?.homeScreenPhotoDarkening || null);

          setStyle({
            backgroundImage: darkening
              ? `linear-gradient(rgba(0, 0, 0, ${darkening}), rgba(0, 0, 0, ${darkening})), url("${img.src}")`
              : `url("${img.src}")`,
          });
        }
      };
      img.src = (data?.type === "file" ? data.file?.httpsUrl : itin.documents?.find(doc => doc.id === data?.imageNodeId)?.file?.httpsUrl) || itin?.photoNode?.file?.httpsUrl;
    }

    return () => {
      isActive = false;
    }
  }, [itin?.homeScreenPhotoDarkening, homeComponent, itin?.photoNode?.file?.httpsUrl, mobileViewGeneric, previewMode, data?.imageNodeId, itin.documents, data?.type, data?.file?.httpsUrl]);

  useEffect(() => {
    setActiveSection(() => {
      if (type === 'gallery') return 'summary';
      if (previewMode && homeComponent) return 'home';
      const [,,,,section] = location.pathname.split('/');
      return section ? section.toLowerCase() : 'summary'
    });
  }, [type, homeComponent, location.pathname, previewMode]);

  const menuItemOptions = {
    accommodation: { type: 'external' },
    flights: {
      path: `${pathStart}/${slideNum}/flights`,
      sectionName: 'flights',
      element: <DailyFlights />
    },
    documents: { type: 'external' },
    posts: {
      path: `${pathStart}/${slideNum}/posts`,
      sectionName: 'posts',
      isOffField: 'hideJournal',
      enabled: !previewMode,
      element: <DailyPosts />
    },
    maps: {
      path: `${pathStart}/${slideNum}/maps`,
      sectionName: 'maps',
      dataField: 'location',
      dataFieldValidationField: 'showInMaps',
      element: <DailyMap />
    },
    weather: {
      path: `${pathStart}/${slideNum}/weather`,
      sectionName: 'weather',
      dataField: 'location',
      dataFieldValidationField: 'showInWeather',
      element: <DailyWeather />
    }
  };

  const navMenuItems = [{
    path: `${pathStart}/${slideNum}`,
    title: t('summary', { lng: itin.language }),
    sectionName: 'summary',
    icon: <img className="nav-item-icon" id="nav-item-summary-icon" src="/icons/summary.svg" alt=""/>,
    element: <DailySummary type={location.pathname.includes('gallery') ? 'gallery' : 'storyboard'}
                           slideData={data} isActive={isActive}/>
  }];

  const renderNavbar = () => {
    if (!Array.isArray(data?.menu) || type === 'gallery') return null;
    if (previewMode && homeComponent) return null;

    const generateNavbar = () => {
      for (const { label, segue, iconId, segueId } of data.menu) {
        if (!menuItemOptions[segue]) throw new Error('Menu Item Not Found: ' + segue);

        const {
          enabled,
          path,
          sectionName,
          type,
          isOffField,
          dataField,
          dataFieldValidationField,
          element
        } = menuItemOptions[segue];
        if (enabled === false) continue;
        if (isOffField && itin[isOffField]) continue;
        if (dataField && dataFieldValidationField && !data?.[dataField]?.[dataFieldValidationField]) continue;

        const icon = <img className="nav-item-icon" alt="" src={icons[iconId]} />
        const item = {
          title: label,
          icon,
          sectionName,
          type,
          path,
          element
        };

        if (segue === 'documents') {
          const doc = itin.documents.find(x => x.id === segueId);

          if (doc?.file?.httpsUrl) {
            delete item.type;
            item.path = `${pathStart}/${slideNum}/view/${doc?.id}`
          } else {
            item.type = 'external';
            item.path = doc?.file?.httpsUrl || doc?.remoteUrl;
          }
        } else if (segue === 'accommodation') {

          const generateAccomodationPath = () => {
            const briefLocation = itin.locations?.find(loc => loc.id === data?.locationId);
            if (!briefLocation.nesting) {
              const pdfNode = itin.documents.find(doc => doc.id === briefLocation?.pdfNodeId);

              if (!pdfNode?.file) return pdfNode?.remoteUrl;
              delete item.type;
              return `${pathStart}/${slideNum}/view/${pdfNode.id}`;
            }

            if (!previewMode) {
              if (viewMode !== 'desktop') delete item.type;
              return viewMode === 'desktop' ? `${window.location.origin}/${itin.id}-${briefLocation.nesting.vamoosId}` : `${pathStart}/${slideNum}/maps/${briefLocation.id}`;
            }

            return generateNestedPreviewUrl(briefLocation.nesting, itin.operatorInfo.code);
          };
          item.path = generateAccomodationPath();
          if (!item.path) continue;
        }

        navMenuItems.push(item);
      }
      if (viewMode !== 'desktop' && activeSection !== 'summary') return;

      const activeIdx = navMenuItems.findIndex(x => x.sectionName === activeSection);
      const items = viewMode === 'desktop' ? navMenuItems : navMenuItems.slice(1);
      return (
        <Navbar
          className="daily-navbar"
          items={items}
          activeIdx={activeIdx === -1 ? 0 : activeIdx}
        />
      )
    }

    if (viewMode !== 'desktop' && activeSection !== 'summary') return generateNavbar();

    return (
      <div className="daily-navbar-container">
        {data?.menu?.length ? generateNavbar() : null}
      </div>
    );
  };

  const renderContent = () => {
    if (!isActive && (activeSection !== 'summary' && activeSection !== 'home')) return null;
    const activeContent = navMenuItems.find(x => x.sectionName === activeSection);
    return previewMode && homeComponent ? <Home /> : activeContent?.element;
  };

  return (
    <div
      className={`slide slide-${viewMode} ${slideNum !== 0 && activeSection !== 'summary' ? 'darkened' : ''} slide-${type}`}>
      { renderNavbar() }
      <div className="slide-background" style={style} />
      <div className={`slide-content slide-content-${activeSection} ${props.previewWithLogo ? 'slide-content-preview-withlogo' : ''} ${previewMode ? '' : `slide-content-${type}`} ${data?.type === 'file' ? 'slide-content-file' : ''}`}>
        <div className={`slide-content-centered slide-content-centered-${activeSection} ${previewMode ? 'preview-mode' : ''}`}>
          {
             renderContent()
          }
        </div>
      </div>
    </div>
  );
};

export default Slide;
